import { useCallback, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import {
  saveDashboard,
  AvailableDashboards,
  deleteDashboard,
  getDashboard,
} from "api/Repos/dashboard"
import {
  AvailableDashboardsResponse,
  GetDashboardResponse,
  SaveDashboardResponse,
  DeleteDashboardResponse,
} from "types/ReportingDashboard/reporting.types"

/**
 * Dashboard file operations for the Reporting view. Imperative: the
 * Reporting container owns dashboard state and its refresh sequencing;
 * save/delete run as react-query mutations, reads are plain authed calls.
 */
interface UseDashboardReturn {
  saveDashboardFile: (
    id: string,
    owner: string,
    dashboardConfig: string,
    name: string,
    overwrite?: boolean
  ) => Promise<SaveDashboardResponse>
  isSaving: boolean
  getProjectDashboards: (
    id: string,
    owner: string
  ) => Promise<AvailableDashboardsResponse>
  isGettingProjectDashboards: boolean
  deleteDashboardFile: (
    id: string,
    owner: string,
    name: string
  ) => Promise<DeleteDashboardResponse>
  isDeleting: boolean
  getDashboardFile: (
    id: string,
    owner: string,
    name: string,
    hintOwner: string
  ) => Promise<GetDashboardResponse>
  isFetching: boolean
}

export const useDashboard = (): UseDashboardReturn => {
  const getAxios = useAuthedAxios()

  const [isGettingProjectDashboards, setIsGettingProjectDashboards] =
    useState(false)
  const [isFetching, setIsFetching] = useState(false)

  const getProjectDashboards = useCallback(
    async (id: string, owner: string): Promise<AvailableDashboardsResponse> => {
      setIsGettingProjectDashboards(true)
      try {
        return await AvailableDashboards(await getAxios(), id, owner)
      } finally {
        setIsGettingProjectDashboards(false)
      }
    },
    [getAxios]
  )

  const getDashboardFile = useCallback(
    async (
      id: string,
      owner: string,
      name: string,
      // Kept for call-site compatibility; the API endpoint has no hint_owner
      // beyond `owner` (the legacy wrapper silently dropped this argument).
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _hintOwner: string
    ): Promise<GetDashboardResponse> => {
      setIsFetching(true)
      try {
        return await getDashboard(await getAxios(), id, owner, name)
      } finally {
        setIsFetching(false)
      }
    },
    [getAxios]
  )

  const saveMutation = useMutation({
    mutationFn: async (args: [string, string, string, string, boolean?]) =>
      saveDashboard(await getAxios(), ...args),
  })

  const deleteMutation = useMutation({
    mutationFn: async (args: [string, string, string]) =>
      deleteDashboard(await getAxios(), ...args),
  })

  const saveDashboardFile = useCallback(
    (
      id: string,
      owner: string,
      dashboardConfig: string,
      name: string,
      overwrite?: boolean
    ) => saveMutation.mutateAsync([id, owner, dashboardConfig, name, overwrite]),
    [saveMutation.mutateAsync]
  )

  const deleteDashboardFile = useCallback(
    (id: string, owner: string, name: string) =>
      deleteMutation.mutateAsync([id, owner, name]),
    [deleteMutation.mutateAsync]
  )

  return {
    saveDashboardFile,
    isSaving: saveMutation.isPending,
    getProjectDashboards,
    isGettingProjectDashboards,
    deleteDashboardFile,
    isDeleting: deleteMutation.isPending,
    getDashboardFile,
    isFetching,
  }
}
